import "./CyberBoxStyles.css";
import { motion, scale, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useOperatorActions } from "../../../../utils/useOperatorActions";

const COLOR_CLASSES = [
  "bg-[#9A1B1E]",
  "bg-[#8B1A1A]",
  "bg-[#7C161A]",
  "bg-[#671314]",
  "bg-[#5C1212]",
  "bg-[#490E0E]",
  "bg-[#3F0C0E]",
  "bg-[#360B0B]",
  "bg-[#250708]",
];

const dur = ({ minX, maxX, speed }) =>
  Math.abs(maxX - minX) / Math.max(1e-6, speed);
export default function CyberBoxComponent({ children }) {
  const speed = 0.6;
  const baseVisible = 3;
  const count = COLOR_CLASSES.length;
  const [activeCount, setActiveCount] = useState(baseVisible);
  const activeRef = useRef(baseVisible);
  const reduceMotion = useReducedMotion();
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const BASE_WIDTH = 1920;
    const BASE_HEIGHT = 1080;

    const updateScale = () => {
      const { innerWidth, innerHeight } = window;
      const widthRatio = innerWidth / BASE_WIDTH;
      const heightRatio = innerHeight / BASE_HEIGHT;

      const newScale = Math.min(widthRatio, heightRatio);

      setScale(newScale);
    };

    updateScale();
    window.addEventListener("resize", updateScale);

    return () => window.removeEventListener("resize", updateScale);
  }, []);
  useOperatorActions("SHOW_INTRO", (action) => {});
  useEffect(() => {
    activeRef.current = activeCount;
  }, [activeCount]);
  const FIRST = {
    minX: -100,
    maxX: 1090 * scale,
    speed: 240 * scale,
    delay: 0,
    startAt: "min",
  };
  const THIRD = {
    minX: -160,
    maxX: 1260 * scale,
    speed: 600 * scale,
    delay: 0.2,
    startAt: "min",
  };
  useEffect(() => {
    let dir = Math.random() < 0.5 ? 1 : -1;
    const tick = () => {
      let cur = activeRef.current;
      if (cur <= baseVisible) dir = 1;
      else if (cur >= count) dir = -1;
      else if (Math.random() < 0.18) dir *= -1;
      const step = Math.random() < 0.25 ? 2 : 1;
      let next = cur + dir * step;
      next = Math.max(baseVisible, Math.min(count, next));
      setActiveCount(next);
    };
    const intervalMs = Math.max(40, 110 / Math.max(0.1, speed));
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [speed, baseVisible, count]);

  const OFF = 0.18;
  const MIN_INTENSITY = 0.85;
  const MAX_INTENSITY = 1.0;
  const GRADIENT_DROP = 0.4;

  const p = (activeCount - baseVisible) / Math.max(1, count - baseVisible);
  const intensity =
    MIN_INTENSITY +
    (MAX_INTENSITY - MIN_INTENSITY) * Math.min(1, Math.max(0, p));

  const itemTransition = {
    type: "spring",
    stiffness: 420,
    damping: 28,
    mass: 0.4,
  };
  const total = 12;
  const perRow = 3;
  const rows = 4;
  const rippleRows = 3;

  const [mode, setMode] = useState("entrance_rows");
  const [activeRow, setActiveRow] = useState(0);
  const [rowToggle, setRowToggle] = useState(false);
  const loopRef = useRef(true);

  const entranceFromX = 120;
  const entranceDur = 0.6;
  const entranceColStagger = 0.08;
  const entranceRowOffset = 0.06;
  const entranceMaxDelay =
    (rippleRows - 1) * entranceRowOffset + (perRow - 1) * entranceColStagger;

  useEffect(() => {
    const id = setTimeout(
      () => setMode("entrance_last"),
      (entranceDur + entranceMaxDelay + 0.2) * 1000
    );
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (mode !== "entrance_last") return;
    const lastBlinkDur = 0.8;
    const id = setTimeout(() => setMode("loop"), lastBlinkDur * 1000 + 100);
    return () => clearTimeout(id);
  }, [mode]);

  useEffect(() => {
    if (mode !== "loop") return;
    (async () => {
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      let row = 0;
      while (loopRef.current) {
        setActiveRow(row);
        setRowToggle((v) => !v);
        const blinkOnce = 0.24;
        const blinks = 3;
        const perCircle = blinkOnce * blinks * 2;
        const stagger = 120;
        const rest = 260;
        const totalMs = perCircle * 1000 + (perRow - 1) * stagger + rest;
        await sleep(totalMs);
        row = (row + 1) % rippleRows;
      }
    })();
    return () => {
      loopRef.current = false;
    };
  }, [mode]);

  const rowBlinkA = { opacity: [1, 0.2, 1, 0.2, 1, 0.2, 1] };
  const rowBlinkB = { opacity: [1, 0.2, 1, 0.2, 1, 0.2, 1] };

  const lastRowCycle = 1.6;
  const lastRowAmp = [70, 70, 70];
  const lastRowStagger = 0.08;
  const lastRowHold = 0.25;
  return (
    <div className="cyber-box-main-container ">
      <div className="cyber-box-outer">
        <div className="cyber-box-top-left-corner">
          <div className="border-container">
            <div className="top-left-left-border"></div>
            <div className="top-left-top-border"></div>
            <div className="top-left-rotated-border"></div>
          </div>
        </div>
        <div className="cyber-box-top-right-corner">
          <div className="border-container">
            <div className="top-right-left-border"></div>
            <div className="top-right-top-border"></div>
            <div className="top-right-rotated-border"></div>
          </div>
        </div>
        <div className="cyber-box-bottom-right-corner">
          <div className="corner-remover-2">
            <div className="overlap-container">
              {COLOR_CLASSES.map((cls, i) => {
                const lit = i < activeCount;
                const t = activeCount > 1 ? i / (activeCount - 1) : 0;
                const gradient = 1 - GRADIENT_DROP * t;
                const opacity = lit ? intensity * gradient : OFF;
                return (
                  <motion.div
                    key={i}
                    className={`overlap-item rounded ${cls}`}
                    style={{ transformOrigin: "bottom center" }}
                    animate={{
                      opacity,
                      scaleY: lit ? 1 : 0.72,
                      filter: lit ? "brightness(1)" : "brightness(0.7)",
                    }}
                    transition={itemTransition}
                  />
                );
              })}
            </div>
          </div>
          <div className="corner-remover-3"></div>

          <div className="border-container">
            <div className="bottom-right-left-border"></div>
            <div className="bottom-right-top-border"></div>
            <div className="bottom-right-rotated-border"></div>
            <div className="bottom-right-rotated-border-2"></div>
            <div className="bottom-right-top-border-2"></div>
          </div>
        </div>
        <div className="cyber-box-bottom-left-corner">
          <div className="border-container">
            <div className="bottom-left-left-border"></div>
            <div className="bottom-left-top-border"></div>
            <div className="bottom-left-rotated-border"></div>
          </div>
        </div>
        <motion.div
          className="cyber-box-inner"
          style={{ transformOrigin: "50% 50%" }}
          initial={{ scale: 0.9, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "tween",
            duration: 2.4,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <motion.div
            className="mouse-that-moves"
            initial={{ x: FIRST.startAt === "max" ? FIRST.maxX : FIRST.minX }}
            animate={{
              x:
                FIRST.startAt === "max"
                  ? [FIRST.maxX, FIRST.minX]
                  : [FIRST.minX, FIRST.maxX],
            }}
            transition={{
              duration: dur(FIRST),
              ease: "linear",
              delay: FIRST.delay,
              repeat: Infinity,
              repeatType: "mirror",
            }}
            style={{ willChange: "transform" }}
          />

          <motion.div
            className="third-mouse-that-moves"
            initial={{ x: THIRD.startAt === "max" ? THIRD.maxX : THIRD.minX }}
            animate={{
              x:
                THIRD.startAt === "max"
                  ? [THIRD.maxX, THIRD.minX]
                  : [THIRD.minX, THIRD.maxX],
            }}
            transition={{
              duration: dur(THIRD),
              ease: ["easeOut"],
              delay: THIRD.delay,
              repeat: Infinity,
              repeatType: "mirror",
            }}
            style={{ willChange: "transform" }}
          />

          <div className="botom-grade-container">
            <div className="bottom-grade-bar">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map(
                (item, index) => (
                  <div key={index} className="bottom-grade-edge"></div>
                )
              )}
              <motion.div
                className="triangle"
                animate={{ x: [0, 865, 0] }}
                transition={{
                  duration: 5,
                  ease: "easeInOut",
                  repeat: Infinity,
                }}
              >
                <div className="inner-triangle" />
              </motion.div>
            </div>
          </div>
          <div className="cyber-box-inner-top-left-corner-box">
            <div className="corner-remove-left-top"></div>
            <div className="patch-left-top"></div>
            <div className="patch-right-top"></div>

            <div className="cyber-box-inner-top-left-corner"></div>
          </div>
          <div className="cyber-box-inner-top-right-corner-box">
            <div className="circle-container">
              {Array.from({ length: total }).map((_, i) => {
                const row = Math.floor(i / perRow);
                const col = i % perRow;

                if (row < rippleRows) {
                  if (mode === "entrance_rows") {
                    const delay =
                      row * entranceRowOffset + col * entranceColStagger;
                    return (
                      <motion.div
                        key={i}
                        className="circle"
                        initial={{ x: -entranceFromX, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{
                          duration: entranceDur,
                          ease: "easeOut",
                          delay,
                        }}
                      />
                    );
                  } else if (mode === "loop") {
                    const isActive = row === activeRow;
                    const perCircle = 0.24 * 3 * 2;
                    const delay = col * 0.12;
                    return (
                      <motion.div
                        key={i}
                        className="circle"
                        animate={
                          isActive
                            ? rowToggle
                              ? rowBlinkA
                              : rowBlinkB
                            : { opacity: 1 }
                        }
                        transition={
                          isActive
                            ? {
                                duration: perCircle,
                                ease: "easeInOut",
                                delay,
                                times: [
                                  0,
                                  1 / 6,
                                  2 / 6,
                                  3 / 6,
                                  4 / 6,
                                  5 / 6,
                                  1,
                                ],
                              }
                            : { duration: 0.2 }
                        }
                      />
                    );
                  } else {
                    return <div key={i} className="circle" />;
                  }
                } else {
                  if (mode === "entrance_last") {
                    return (
                      <motion.div
                        key={i}
                        className="circle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 1, 0.3, 1] }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                      />
                    );
                  } else {
                    const j = col;
                    const amp = lastRowAmp[j];
                    const down = lastRowCycle * 0.4;
                    const holdDown = lastRowHold;
                    const up = lastRowCycle * 0.4;
                    const holdTop = lastRowHold;
                    const delay = j * lastRowStagger;
                    return (
                      <motion.div
                        key={i}
                        className="circle"
                        animate={{ y: [0, amp, amp, 0, 0] }}
                        transition={{
                          duration: down + holdDown + up + holdTop,
                          ease: "easeInOut",
                          times: [0, 0.4, 0.6, 0.9, 1],
                          repeat: Infinity,
                          repeatDelay: 0,
                          delay,
                        }}
                      />
                    );
                  }
                }
              })}
            </div>
            <div className="corner-remove-left-top1"></div>
            <div className="patch-left-top1"></div>
            <div className="patch-right-top1"></div>

            <div className="cyber-box-inner-top-right-corner"></div>
          </div>
          <div className="cyber-box-inner-bottom-left-corner-box">
            <div className="corner-remove-left-top2"></div>
            <div className="patch-left-top2"></div>
            <div className="patch-right-top2"></div>

            <div className="cyber-box-inner-bottom-left-corner"></div>
          </div>
          <div className="cyber-box-inner-bottom-right-corner-box">
            <div className="cyber-box-inner-bottom-right-corner"></div>
            <div className="cyber-box-inner-bottom-right-left-1"></div>
            <div className="cyber-box-inner-bottom-right-left-2">
              <div className="corner-remover-1"></div>
            </div>
          </div>
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: "center center",
            }}
            className="content-container p-[40px] flex items-center justify-center w-full h-full "
          >
            {children}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
